import {Utils} from "./Utils";
import {Popup} from "./Popup";
import {Textarea} from "./Textarea";
import {Sortable} from "./Sortable";
import {Merged} from "./Merged";
import {Tagged} from "./Tagged";

interface PanelConfig {
    popup: Popup;
    saveURL: string;
    elements: {
        form: string;
        save: string;
        spinner: string;
        tabs: string;
        pages: string,
        groupTabs?: string,
        groups?: string
    }
}

type ServerResponse =  {
    status: string;
    data: string[]
}

type BlockData = {
    name: string,
    title: string,
    id: string
}

export class Panel {
    private popup: Popup;
    public saveURL: string;
    private readonly elements: {
        form: HTMLFormElement;
        save: HTMLElement;
        spinner: HTMLElement;
        tabs: NodeListOf<HTMLElement>;
        pages: NodeListOf<HTMLElement>;
        groupTabs?: NodeListOf<HTMLElement>;
        groups?: NodeListOf<HTMLElement>;
        sortable?: NodeListOf<HTMLElement>;
        tagged?: any[];
    };
    private selectors: {
        tabs: string;
        groups?: string;
    };
    protected state: {
        isReloading: boolean;
        currentPage?: HTMLElement;
        copyAnimation?: boolean;
    };

    constructor({popup, saveURL, elements}: PanelConfig) {
        this.popup = popup;
        this.saveURL = saveURL;
        this.elements = {
            form: document.querySelector(elements.form),
            save: document.querySelector(elements.save),
            spinner: document.querySelector(elements.spinner),
            tabs: document.querySelectorAll(elements.tabs),
            pages: document.querySelectorAll(elements.pages),
            groupTabs: document.querySelectorAll(elements.groupTabs),
            groups: document.querySelectorAll(elements.groups),
            tagged: []
        };
        this.selectors = {
            tabs: elements.tabs,
            groups: elements.groups
        };
        this.state = {
            isReloading: false
        };
        this.init();
    }

    public static switchElement(elements: NodeListOf<HTMLElement>, target: HTMLElement): void {
        elements.forEach((it) => {
            it === target
                ? it.classList.add(`is-active`)
                : it.classList.remove(`is-active`);
        });
    }

    public findByAttribute(elements: NodeListOf<HTMLElement>, attr: string, bookmark: string): HTMLElement {
        return Array.from(elements).find(it => {
            return it.getAttribute(attr) === bookmark;
        });
    }

    public addSortable(selector: string): Sortable {
        if (!this.elements.form) return;

        const template = ((el: HTMLElement, i: number): string => {
            return `\"${i}\":{
                \"id\":\"${el.getAttribute(`data-id`)}\",
                \"name\":\"${el.getAttribute(`data-name`)}\",
                \"title\":\"${el.getAttribute(`data-title`)}\",
                \"sort\":\"${i}\",\"enable\":\"${el.getAttribute(`data-enable`)}\",
                \"settings\":\"${el.getAttribute(`data-settings`)}\"
            },`
        });
        this.elements.sortable = document.querySelectorAll(selector);
        this.elements.sortable.forEach(list => new Sortable({list, template}));
    }

    public addMerged(selector: string): void {
        const elements = document.querySelectorAll(selector);
        elements.forEach(element => {
            const merged = {
                textarea: element.querySelectorAll(`textarea`),
                storage: element.previousElementSibling as HTMLInputElement
            };
            new Merged(merged);
        });
    }

    public addTagged(selector: string): void {
        const tagged = document.querySelectorAll(selector);
        tagged.forEach((it) => {
            const element = new Tagged({
                image: it.parentElement.nextElementSibling.querySelector(`img`),
                storage: it.previousElementSibling as HTMLInputElement,
                amount: 3
            });
            this.elements.tagged.push(element);
        });
    }

    public addAction(selector: string, type: string, element?: string): void {
        if (!this.elements.form) return;
        const elements = document.querySelectorAll(selector);
        elements.forEach(action => action.addEventListener( 'click', () => {
            if (type === `edit`) this.onEditActionClick(action);
            if (type === `code`) this.onViewCodeActionClick(action, element);
            if (type === `disable`) this.onDisableActionClick(action, element);
            if (type === `copy`) this.onCopyActionClick(action, element);
        }, false ));
    }

    protected onEditActionClick(trigger: Element): void {
        const
            {groups, tabs} = this.elements,
            bookmark = trigger.getAttribute(`data-block`),
            group = this.findByAttribute(groups, `data-group`, `divider_homepage`),
            tab = this.findByAttribute(tabs, `data-tab`, bookmark);
        if (group && tab) this.switchGroup(group, tab, `divider_homepage`);
    }

    protected onViewCodeActionClick(trigger: Element, element: string): void {
        const json = trigger.getAttribute(`data-block`);
        if (!json) return;

        const
            data = JSON.parse(json),
            props = window.theme.blocks[data.name],
            container = document.querySelector(element),
            title = container.querySelector(`h2`),
            code = container.querySelector(`[role=log]`),
            template = (data: BlockData): string => {
                return (Utils.getLang() === `ru_ru`)
                    ? `Блок «${data.title}» – код `
                    : `Block "${data.name}" – code`;
            };

        if (props) {
            title.innerText = template(data);
            code.innerHTML = this.renderSmarty(data, props);
            this.switchTab(trigger as HTMLElement, false);
        }
    }

    public renderSmarty(data: BlockData, object: {[key: string]: any}): string {
        return `
            <p class="panel-code__comment">&lt;!--
                ${Utils.getLang() === `ru_ru`
                    ? `Блок «` + data.title
                    : `Block «` + data.name.replace(/(^|\s)\S/g, l => l.toUpperCase())}»
                --&gt;
            </p>
            <span class="panel-code__line">{$props = [</span>
                ${this.renderProperties(object)}
            <span class="panel-code__line">]}</span>
            <br>
            <p>{call add_block block="<span class="is-highlighted">${data.id}</span>" props=$props}</p>
        `.trim()
    }

    public renderProperties(object: {[key: string]: any}): string {
        let result = ``,
            i = 1;
        getProp(object);

        function getProp(o: {[key: string]: any}) {
            for (let p in o) {
                let keys = Object.keys(o);
                let last = keys[keys.length - 1];

                if (typeof(o[p]) === 'object') {
                    result += `<span class="panel-code__line">${'    '.repeat(i)}'<span class="is-highlighted">${p}</span>' => [</span>`;
                    i++;
                    getProp(o[p]);
                    i--;
                    result += `<span class="panel-code__line">${'    '.repeat(i)}]${p === last ? '' : ','}</span>`;
                } else {
                    result += `<span class="panel-code__line">${'    '.repeat(i)}'<span class="is-highlighted">${p}</span>' => '${Utils.escapeHTML(o[p])}'${p === last ? '' : ','}</span>`;
                }
            }
        }
        return result;
    }

    protected onDisableActionClick(trigger: Element, spinner?: string): void {
        if (this.state.isReloading) return;

        const
            {sortable} = this.elements,
            spnr = trigger.parentElement.querySelector(spinner) as HTMLElement,
            block = trigger.getAttribute(`data-block`);
        let checkbox: HTMLInputElement = undefined;

        for (let it of sortable) {
            const cb = it.querySelectorAll(`[type=checkbox]`);
            if (cb.length) checkbox = [...cb].find(it => it.id === `${block}-availability`) as HTMLInputElement;
            if (checkbox) break;
        }

        if (checkbox) {
            checkbox.click();
            Utils.addClass(spnr, `is-active`);
            trigger.remove();
            this.save(1500);
        }
    }

    protected onCopyActionClick(trigger: Element, target: string): void {
        if (this.state.copyAnimation) return;
        const
            checkmark = trigger.previousElementSibling as HTMLElement,
            content = document.querySelector(target);

        this.state.copyAnimation = true;
        Utils.copy(content)
            .then(() => {
                checkmark.classList.add(`is-animated`);

                setTimeout(() => {
                    checkmark.classList.remove(`is-animated`);
                    this.state.copyAnimation = false;
                }, 1700);
            })
            .catch((err: Error) => {
                console.warn(`The copy function requires a secure origin — either HTTPS or localhost.`);
                this.state.copyAnimation = false;
            });
    }

    protected onGroupTabClick(trigger: HTMLElement): void {
        const group: HTMLElement = trigger.closest(this.selectors.groups);
        const target: HTMLElement = group.querySelector(this.selectors.tabs);
        const bookmark = group.getAttribute(`data-group`);

        this.switchGroup(group, target, bookmark, true);
    }

    public switchGroup(group: HTMLElement, target: HTMLElement, bookmark: string, toggle = false): void {
        toggle
            ? Utils.toggleClass(group, `is-active`)
            : Utils.addClass(group, `is-active`);
        if (group.classList.contains(`is-active`)) {
            Utils.setCookie('themesettings_group', bookmark);
            this.switchTab(target);
        }
    }

    public switchTab(trigger: HTMLElement, cookie: boolean = true): void {
        const {tabs, pages, tagged} = this.elements;
        const bookmark = trigger.getAttribute(`data-tab`);
        const page = this.findByAttribute(pages, `data-tab`, bookmark);

        Panel.switchElement(tabs, trigger);
        Panel.switchElement(pages, page);
        if (cookie) Utils.setCookie(`themesettings_tab`, bookmark);
        this.state.currentPage = page;
        this.resizeTextarea();
        tagged.forEach(it => it.updateElements());
    }

    protected initCurrentPage(): void {
        const pages = this.elements.pages;
        this.state.currentPage = Array.from(pages).find(page => page.matches(`.is-active`));
        if (this.state.currentPage) this.resizeTextarea();
    }

    public resizeTextarea(): Textarea {
        const page = this.state.currentPage;
        if (page.hasAttribute(`resized`)) return;
        page.setAttribute(`resized`, ``);
        return new Textarea({
            elements: `textarea`,
            container: page
        });
    }

    protected onSaveClick(evt: Event): void {
        evt.preventDefault();
        if (!this.state.isReloading) this.save(1500);
    }

    protected onSaveKeyPressing(evt: KeyboardEvent): void {
        if (evt.code === `KeyS` && (evt.ctrlKey || evt.metaKey)) {
            if (this.popup.state === `closed`) return;
            evt.preventDefault();

            this.elements.save.focus();
            if (!this.state.isReloading) this.save(1500);
        }
    }

    public save(timeout: number): void {
        this.state.isReloading = true;
        fetch(this.saveURL, {
            method: 'POST',
            body: new FormData(this.elements.form)
        })
            .then(response => response.json())
            .then(response => this.reload(timeout, response))
            .catch(error => console.error('Error:', error));
    }

    protected reload(timeout: number, response: ServerResponse): void {
        Utils.addClass(this.elements.spinner, `is-active`);
        if (response.status === `ok`) {
            setTimeout(() => location.reload(), timeout);
        }
    }

    protected bind(): void {
        const {save, tabs, groupTabs} = this.elements;
        document.addEventListener('keydown', (evt) => this.onSaveKeyPressing(evt));
        save.addEventListener( 'click', (evt) => this.onSaveClick(evt), false );
        tabs.forEach(tab => tab.addEventListener( 'click', () => this.switchTab(tab), false ));
        groupTabs.forEach(tab => tab.addEventListener( 'click', () => this.onGroupTabClick(tab), false ));
    }

    protected init(): void {
        if (!this.elements.form) return;
        this.initCurrentPage();
        this.bind();
    }
}